import { useCallback, useEffect, useMemo, useState } from 'react'

import Grid from '@mui/material/Grid2'
import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import DeleteIcon from '@mui/icons-material/Delete'

import Options from './Options'
import { FormCheckbox, FormSelect, FormTextField, getIn, useFormikContext } from '../Formik/forms'
import { constantize } from '../../lib/utils'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import MainContainer from 'components/layout/MainContainer/MainContainer'
import { useFeatures } from 'providers/FeaturesProvider'
import { FIELD_CONFLICT_MESSAGE } from './validateFields'

const FIELD_TYPES = [
  { text: 'First Name', value: 'first_name' },
  { text: 'Last Name', value: 'last_name' },
  { text: 'Phone', value: 'phone' },
  { text: 'Email', value: 'email' },
  { text: 'Street Address', value: 'street_address' },
  { text: 'City', value: 'city' },
  { text: 'State', value: 'state' },
  { text: 'ZIP code', value: 'zip_code', field_name: 'ZIP_Code' },
  { text: 'SubID', value: 'subid', field_name: 'subid' },
  { text: 'Source', value: 'source', field_name: 'source' },
  { text: 'IP Address', value: 'remote_ip', field_name: 'ipAddress' },
  { text: 'TCPA Disclosure', value: 'tcpa_disclosure' },
  { text: 'ComplyEd Creative ID', value: 'complyed_creative_id', field_name: 'complyed_creative_id' },
  { text: 'Trusted Form Cert URL', value: 'trusted_form_cert_url', field_name: 'xxTrustedFormCertUrl' },
  { text: 'Jornaya LeadID', value: 'jornaya_leadid_token', field_name: 'jornaya_leadid_token' },
  { text: 'Custom drop-down', value: 'custom_select' },
  { text: 'Custom free-text', value: 'custom_text' },
]

const CUSTOM_FIELD_TYPES = ['custom_select', 'custom_text', 'custom_bigint', 'custom_float']

const FIELD_TYPE_TO_NAME = FIELD_TYPES
  .reduce((mapping, { value, field_name }) => { if(field_name) { mapping[value] = field_name } return mapping }, {})

export default function Field({item: field, path, onRemove, permitEdit}) {
  const { setFieldValue, handleChange } = useFormikContext()
  const { isFeatureFlagEnabled } = useFeatures()
  const fieldType = useMemo(() => getIn(field, 'field_type'), [field])
  const defaultName = useMemo(() => FIELD_TYPE_TO_NAME[fieldType] || constantize(fieldType), [fieldType])
  const name = useMemo(() => getIn(field, 'name'), [field])
  const handleNameChange = useCallback(event => { setCustomName(true); handleChange(event) }, [handleChange])
  const isCustom = useMemo(() => CUSTOM_FIELD_TYPES.includes(fieldType), [fieldType])
  const showLeadsTableCheckbox = isCustom && isFeatureFlagEnabled('lead_table_custom_columns')
  const [customName, setCustomName] = useState(() => !!name && name != defaultName)

  useEffect(() => {
    if(!isCustom && !customName) {
      setFieldValue(`${path}.name`, defaultName)
    }
  }, [fieldType, isCustom, customName, path, setFieldValue, defaultName])

  return (
    <MainContainer>
      <Box sx={{ '.MuiTextField-root': { marginBottom: '4px' }, '.MuiCheckbox-root': { marginY: '0px' } }} >
        <Grid container alignItems="center" spacing={2}>
          <Grid>
            <FormSelect
              sx={{width: 200}}
              label="Type"
              name={`${path}.field_type`}
              disabled={!permitEdit}
            >
              {FIELD_TYPES.map(({text, value}, index) => (
                <MenuItem key={index} value={value}>{text}</MenuItem>
              ))}
            </FormSelect>
          </Grid>
          <Grid sx={{width: 200}}>
            <FormTextField
              label="Name"
              name={`${path}.name`}
              onChange={handleNameChange}
              disabled={!permitEdit}
            />
          </Grid>
          <Grid sx={{flexGrow: 1}}>
            <FormTextField
              label="Question text"
              name={`${path}.description`}
              disabled={!permitEdit}
            />
          </Grid>
          <Grid>
            <Tooltip title="Requires this field to be included in lead posts to VEGA">
              <span>
                <FormCheckbox
                  label="Required?"
                  name={`${path}.required`}
                  disabled={!permitEdit}
                />
              </span>
            </Tooltip>
          </Grid>
          <Grid>
            <Tooltip title="Allows this field to be used for grouping and aggregating data in tables and reports">
              <span>
                <FormCheckbox
                  label="Groupable?"
                  name={`${path}.groupable`}
                  disabled={!permitEdit}
                />
              </span>
            </Tooltip>
          </Grid>
          <Grid>
            <Tooltip title="Hides this field from Posting Instructions and Vendor view">
              <span>
                <FormCheckbox
                  label="Private?"
                  name={`${path}.private`}
                  disabled={!permitEdit}
                />
              </span>
            </Tooltip>
          </Grid>
          {showLeadsTableCheckbox && (
            <Grid>
              <Tooltip title="Makes this field available as a column in the Leads table">
                <span>
                  <FormCheckbox
                    label="LeadsTable?"
                    name={`${path}.leads_table_enabled`}
                    disabled={!permitEdit}
                  />
                </span>
              </Tooltip>
            </Grid>
          )}
          <Grid>
            <IconButton onClick={onRemove} aria-label="Remove field" disabled={!permitEdit}>
              <DeleteIcon/>
            </IconButton>
          </Grid>
        </Grid>
        {getIn(field, 'private') && getIn(field, 'required') && (
          <Typography sx={{ color: 'error.main', ml: 2 }}>{FIELD_CONFLICT_MESSAGE}</Typography>
        )}
        {fieldType === 'custom_select' && (
          <Options
            path={`${path}.options`}
            permitEdit={permitEdit}
          />
        )}
      </Box>
    </MainContainer>
  )
}
